import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { costingsAPI, partsAPI, processFlowsAPI, bomAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { 
  Calculator, Info, Loader2, Search, Package, Boxes, AlertTriangle, Check,
} from 'lucide-react';

export function NewCostingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const partIdFromUrl = searchParams.get('part');
  const inquiryIdFromUrl = searchParams.get('inquiry');
  const inquiryProductIdFromUrl = searchParams.get('product');
  
  const [part, setPart] = useState(null);
  const [partSearch, setPartSearch] = useState('');
  const [partResults, setPartResults] = useState([]);
  const [searchingParts, setSearchingParts] = useState(false);
  
  const [processFlow, setProcessFlow] = useState(null);
  const [bom, setBom] = useState(null);
  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);

  // Component costing form
  const [formData, setFormData] = useState({
    quantity: 1000,
    rm_grade: '',
    rm_price_per_kg: '',
    scrap_percentage: '5',
    scrap_recovery_per_kg: '20',
    irr_percentage: '0.5',
    crr_percentage: '0.1',
    inspection_testing_percentage: '0.5',
    packaging_cost_per_piece: '0',
    logistics_cost_per_piece: '0',
    overhead_percentage: '10',
    finance_cost_percentage: '1',
    risk_cost_percentage: '0.5',
    margin_percentage: '15',
  });

  // Kit costing form
  const [kitFormData, setKitFormData] = useState({
    quantity: 1,
    assembly_labor_cost: '0',
    packaging_cost: '0',
    logistics_cost: '0',
    overhead_percentage: '10',
    finance_cost_percentage: '1',
    risk_cost_percentage: '0.5',
    margin_percentage: '15',
  });

  const fetchPartById = useCallback(async (partId) => {
    setLoading(true);
    try {
      const res = await partsAPI.get(partId);
      selectPart(res.data.part || res.data);
    } catch (e) {
      toast.error('Failed to load part');
    } finally {
      setLoading(false);
    }
  }, []);

  const searchParts = useCallback(async () => {
    setSearchingParts(true);
    try {
      const res = await partsAPI.search(partSearch);
      setPartResults(res.data?.items || res.data || []);
    } catch (e) {
    } finally {
      setSearchingParts(false);
    }
  }, [partSearch]);

  useEffect(() => {
    if (partIdFromUrl) {
      fetchPartById(partIdFromUrl);
    }
  }, [partIdFromUrl, fetchPartById]);

  useEffect(() => {
    if (partSearch.length >= 2 && !part) {
      searchParts();
    } else {
      setPartResults([]);
    }
  }, [partSearch, part, searchParts]);

  const selectPart = async (selectedPart) => {
    setPart(selectedPart);
    setPartSearch('');
    setPartResults([]);
    
    // Set RM Grade from part
    if (selectedPart.grade) {
      setFormData(prev => ({ ...prev, rm_grade: selectedPart.grade }));
    }
    
    // Fetch process flow or BOM based on part category
    try {
      if (selectedPart.part_category === 'kit') {
        // Fetch BOM for Kit — the kit price comes from the BOM, not from manual entry
        const bomRes = await bomAPI.getByPart(selectedPart.id);
        if (bomRes.data?.items?.length > 0) {
          const detail = await bomAPI.get(bomRes.data.items[0].id);
          setBom(detail.data);
        } else {
          toast.warning('No BOM found for this Kit. Create a BOM first.');
        }
      } else {
        // Fetch Process Flow for Component
        const flowRes = await processFlowsAPI.list({ part_id: selectedPart.id, is_active: true });
        if (flowRes.data?.items?.length > 0) {
          setProcessFlow(flowRes.data.items[0]);
        } else {
          toast.warning('No Process Flow found for this Part. Create a Process Flow first.');
        }
      }
    } catch (e) {
    }
  };

  const handleSubmitComponent = async (e) => {
    e.preventDefault();
    if (!part || !processFlow) {
      toast.error('Please select a part with a process flow');
      return;
    }

    setCalculating(true);
    try {
      const netWeightGrams = Number(part.net_weight_grams);
      if (!netWeightGrams) {
        toast.error(`Add Net Weight (g) in Part Master for ${part.internal_part_no} — material cost needs the weight per piece`);
        setCalculating(false);
        return;
      }
      const data = {
        part_id: part.id,
        inquiry_id: inquiryIdFromUrl || null,
        inquiry_product_id: inquiryProductIdFromUrl || null,
        process_flow_id: processFlow.id,
        quantity: parseFloat(formData.quantity),
        net_weight_grams: netWeightGrams,
        rm_grade: formData.rm_grade || null,
        rm_price_per_kg: parseFloat(formData.rm_price_per_kg),
        scrap_percentage: parseFloat(formData.scrap_percentage),
        scrap_recovery_per_kg: parseFloat(formData.scrap_recovery_per_kg),
        irr_percentage: parseFloat(formData.irr_percentage),
        crr_percentage: parseFloat(formData.crr_percentage),
        inspection_testing_percentage: parseFloat(formData.inspection_testing_percentage),
        packaging_cost_per_piece: parseFloat(formData.packaging_cost_per_piece),
        logistics_cost_per_piece: parseFloat(formData.logistics_cost_per_piece),
        overhead_percentage: parseFloat(formData.overhead_percentage),
        finance_cost_percentage: parseFloat(formData.finance_cost_percentage),
        risk_cost_percentage: parseFloat(formData.risk_cost_percentage),
        margin_percentage: parseFloat(formData.margin_percentage),
      };

      const response = await costingsAPI.create(data);
      toast.success(`Costing ${response.data.costing_number} created`);
      navigate(`/costings/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Calculation failed');
    } finally {
      setCalculating(false);
    }
  };

  const handleSubmitKit = async (e) => {
    e.preventDefault();
    if (!part || !bom) {
      toast.error('Please select a Kit with a BOM');
      return;
    }

    setCalculating(true);
    try {
      const data = {
        part_id: part.id,
        inquiry_id: inquiryIdFromUrl || null,
        inquiry_product_id: inquiryProductIdFromUrl || null,
        bom_id: bom.id,
        quantity: parseFloat(kitFormData.quantity),
        quantity_unit: 'kit',
      };

      const response = await costingsAPI.createKit(data);
      toast.success(`Kit Costing ${response.data.costing_number} created`);
      navigate(`/costings/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Calculation failed');
    } finally {
      setCalculating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-3xl font-heading font-bold uppercase tracking-tight">
          New Costing
        </h1>
        <p className="text-muted-foreground">Calculate manufacturing cost (Part-Centric)</p>
      </div>

      {/* Part Selection */}
      <Card className="card-dashboard">
        <CardHeader>
          <CardTitle className="text-lg font-heading uppercase">Select Part *</CardTitle>
          <CardDescription>Search and select a Part to cost</CardDescription>
        </CardHeader>
        <CardContent>
          {!part ? (
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search parts by part number or description..."
                value={partSearch}
                onChange={(e) => setPartSearch(e.target.value)}
                className="pl-9"
                data-testid="part-search-input"
              />
              {searchingParts && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                </div>
              )}
              {partResults.length > 0 && (
                <div className="absolute z-10 w-full mt-1 border rounded-lg bg-card max-h-60 overflow-y-auto shadow-lg">
                  {partResults.map((p) => (
                    <div
                      key={p.id}
                      className="p-3 hover:bg-muted/50 cursor-pointer border-b last:border-0"
                      onClick={() => selectPart(p)}
                      data-testid={`part-option-${p.id}`}
                    >
                      <div className="flex justify-between items-center">
                        <span className="font-mono font-medium">{p.internal_part_no}</span>
                        <Badge variant="outline" className="text-xs">
                          {p.part_category === 'kit' ? (
                            <><Boxes className="h-3 w-3 mr-1" />Kit</>
                          ) : (
                            <><Package className="h-3 w-3 mr-1" />Component</>
                          )}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">{p.description}</p>
                      {p.grade && <Badge variant="secondary" className="mt-1 text-xs">{p.grade}</Badge>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-mono font-bold text-lg">{part.internal_part_no}</p>
                  <p className="text-muted-foreground">{part.description}</p>
                  <div className="flex gap-2 mt-2">
                    <Badge variant="outline">
                      {part.part_category === 'kit' ? (
                        <><Boxes className="h-3 w-3 mr-1" />Kit</>
                      ) : (
                        <><Package className="h-3 w-3 mr-1" />Component</>
                      )}
                    </Badge>
                    {part.grade && <Badge variant="secondary">{part.grade}</Badge>}
                    {part.net_weight_grams && (
                      <Badge variant="outline">{part.net_weight_grams}g</Badge>
                    )}
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => { setPart(null); setProcessFlow(null); setBom(null); }}>
                  Change
                </Button>
              </div>
              
              {/* Process Flow / BOM Status */}
              <div className="mt-4 pt-4 border-t">
                {part.part_category === 'kit' ? (
                  bom ? (
                    <div className="flex items-center gap-2 text-green-600">
                      <Check className="h-4 w-4" />
                      <span>BOM found: {bom.items?.length || 0} child parts</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-amber-600">
                      <AlertTriangle className="h-4 w-4" />
                      <span>No BOM found. </span>
                      <Button variant="link" className="p-0 h-auto" onClick={() => navigate(`/bom/new?part=${part.id}`)}>
                        Create BOM
                      </Button>
                    </div>
                  )
                ) : (
                  processFlow ? (
                    <div className="flex items-center gap-2 text-green-600">
                      <Check className="h-4 w-4" />
                      <span>Process Flow found: {processFlow.steps?.length || 0} steps</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-amber-600">
                      <AlertTriangle className="h-4 w-4" />
                      <span>No Process Flow found. </span>
                      <Button variant="link" className="p-0 h-auto" onClick={() => navigate(`/process-flows/new?part=${part.id}`)}>
                        Create Process Flow
                      </Button>
                    </div>
                  )
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Component Costing Form */}
      {part && part.part_category !== 'kit' && processFlow && (
        <form onSubmit={handleSubmitComponent} className="space-y-6">
          {/* Quantity */}
          <Card className="card-dashboard">
            <CardHeader>
              <CardTitle className="text-lg font-heading uppercase">Costing Parameters</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Costing Quantity *</Label>
                  <Input
                    type="number"
                    value={formData.quantity}
                    onChange={(e) => setFormData(prev => ({ ...prev, quantity: e.target.value }))}
                    required
                    data-testid="quantity-input"
                  />
                  <p className="text-xs text-muted-foreground">Order quantity for costing</p>
                </div>
                <div className="space-y-2">
                  <Label>Net Weight (g)</Label>
                  <Input
                    type="number"
                    value={part.net_weight_grams || ''}
                    disabled
                    className="bg-muted"
                    data-testid="costing-net-weight-input"
                  />
                  {Number(part.net_weight_grams) ? (
                    <p className="text-xs text-muted-foreground">From Part Master</p>
                  ) : (
                    <p className="text-xs font-medium text-amber-700" data-testid="costing-net-weight-missing">
                      Missing in Part Master — add the weight per piece before costing
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Process Steps</Label>
                  <Input
                    value={processFlow.steps?.length || 0}
                    disabled
                    className="bg-muted"
                  />
                  <p className="text-xs text-muted-foreground">From Process Flow</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Raw Material Parameters */}
          <Card className="card-dashboard">
            <CardHeader>
              <CardTitle className="text-lg font-heading uppercase">Raw Material</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label>RM Grade</Label>
                  <Input
                    type="text"
                    placeholder="e.g., 10B21, SS316"
                    value={formData.rm_grade}
                    onChange={(e) => setFormData(prev => ({ ...prev, rm_grade: e.target.value }))}
                    data-testid="rm-grade-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Raw Material Price (₹/kg) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.rm_price_per_kg}
                    onChange={(e) => setFormData(prev => ({ ...prev, rm_price_per_kg: e.target.value }))}
                    required
                    data-testid="rm-price-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Scrap %</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={formData.scrap_percentage}
                    onChange={(e) => setFormData(prev => ({ ...prev, scrap_percentage: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Scrap Recovery (₹/kg)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.scrap_recovery_per_kg}
                    onChange={(e) => setFormData(prev => ({ ...prev, scrap_recovery_per_kg: e.target.value }))}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Quality & Rejection */}
          <Card className="card-dashboard">
            <CardHeader>
              <CardTitle className="text-lg font-heading uppercase">Quality & Rejection</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Internal Rejection Rate (IRR) %</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={formData.irr_percentage}
                    onChange={(e) => setFormData(prev => ({ ...prev, irr_percentage: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Customer Return Rate (CRR) %</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={formData.crr_percentage}
                    onChange={(e) => setFormData(prev => ({ ...prev, crr_percentage: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Inspection & Testing %</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={formData.inspection_testing_percentage}
                    onChange={(e) => setFormData(prev => ({ ...prev, inspection_testing_percentage: e.target.value }))}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Packaging & Logistics */}
          <Card className="card-dashboard">
            <CardHeader>
              <CardTitle className="text-lg font-heading uppercase">Packaging & Logistics</CardTitle>
              <p className="text-xs text-muted-foreground">Enter rates per Kg - will be converted to per piece using Net Weight</p>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Packaging Cost (₹/Kg)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.packaging_cost_per_piece}
                    onChange={(e) => setFormData(prev => ({ ...prev, packaging_cost_per_piece: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Logistics Cost (₹/Kg)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.logistics_cost_per_piece}
                    onChange={(e) => setFormData(prev => ({ ...prev, logistics_cost_per_piece: e.target.value }))}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Overheads & Margin */}
          <Card className="card-dashboard">
            <CardHeader>
              <CardTitle className="text-lg font-heading uppercase">Overheads, Finance & Margin</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label>Factory Overhead %</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={formData.overhead_percentage}
                    onChange={(e) => setFormData(prev => ({ ...prev, overhead_percentage: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Finance Cost %</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={formData.finance_cost_percentage}
                    onChange={(e) => setFormData(prev => ({ ...prev, finance_cost_percentage: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Risk Premium %</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={formData.risk_cost_percentage}
                    onChange={(e) => setFormData(prev => ({ ...prev, risk_cost_percentage: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Target Margin % *</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={formData.margin_percentage}
                    onChange={(e) => setFormData(prev => ({ ...prev, margin_percentage: e.target.value }))}
                    required
                    data-testid="margin-input"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex justify-end gap-4">
            <Button type="button" variant="outline" onClick={() => navigate('/costings')}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              className="btn-primary" 
              disabled={calculating}
              data-testid="calculate-costing-btn"
            >
              <Calculator className="mr-2 h-4 w-4" />
              {calculating ? 'Calculating...' : 'Calculate Costing'}
            </Button>
          </div>
        </form>
      )}

      {/* Kit Costing (price built from the BOM) */}
      {part && part.part_category === 'kit' && bom && (
        <form onSubmit={handleSubmitKit} className="space-y-6" data-testid="kit-costing-form">
          <Card className="card-dashboard">
            <CardHeader>
              <CardTitle className="text-lg font-heading uppercase">Kit price from BOM</CardTitle>
              <CardDescription>
                Child parts are already costed with their own margin. Kitting only adds labour and packaging —
                no further margin is applied on a kit. Edit labour/packaging on the BOM itself.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="table-header">
                    <TableHead>Part No</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Qty/Kit</TableHead>
                    <TableHead>Costing</TableHead>
                    <TableHead>Selling price / pc</TableHead>
                    <TableHead>Line cost / kit</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(bom.pricing?.lines || []).map((line) => (
                    <TableRow key={line.child_part_id} className="table-row" data-testid={`kit-bom-line-${line.child_part_id}`}>
                      <TableCell className="font-mono">{line.child_part_no}</TableCell>
                      <TableCell>{line.child_description}</TableCell>
                      <TableCell className="font-mono">{line.quantity_per_kit}</TableCell>
                      <TableCell className="text-xs font-mono">{line.child_costing_number || '—'}</TableCell>
                      <TableCell className="font-mono">
                        {line.child_price_per_piece != null
                          ? `₹${line.child_price_per_piece.toFixed(4)}`
                          : <span className="text-red-600">{line.blocked_reason}</span>}
                      </TableCell>
                      <TableCell className="font-mono">
                        {line.line_cost != null ? `₹${line.line_cost.toFixed(4)}` : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {!bom.pricing?.is_priceable && (
                <div className="mt-4 border border-red-300 bg-red-50 p-3 text-sm text-red-800" data-testid="kit-price-blocked">
                  <div className="flex items-center gap-2 font-semibold">
                    <AlertTriangle className="h-4 w-4" /> The kit price cannot be built yet
                  </div>
                  <ul className="mt-2 list-disc pl-6">
                    {(bom.pricing?.missing_children || []).map((child) => (
                      <li key={child.child_part_id} data-testid={`kit-missing-child-${child.child_part_id}`}>
                        <span className="font-mono">{child.child_part_no || child.child_description}</span> — {child.reason}
                      </li>
                    ))}
                    {(bom.pricing?.lines || []).length === 0 && <li>This BOM has no child parts yet.</li>}
                  </ul>
                  <Button type="button" variant="link" className="mt-1 h-auto p-0"
                    onClick={() => navigate(`/bom/${bom.id}`)} data-testid="kit-open-bom-button">Open the BOM</Button>
                </div>
              )}

              {bom.pricing?.is_priceable && (
                <div className="mt-4 grid gap-3 border bg-slate-50 p-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="kit-price-buildup">
                  <div><p className="text-xs uppercase text-muted-foreground">Components / kit</p>
                    <p className="font-mono text-lg" data-testid="kit-components-cost">&#8377;{bom.pricing.components_cost_per_kit.toFixed(4)}</p></div>
                  <div><p className="text-xs uppercase text-muted-foreground">Kitting labour</p>
                    <p className="font-mono text-lg" data-testid="kit-labour-cost">&#8377;{bom.pricing.kitting_labor_cost.toFixed(2)}</p></div>
                  <div><p className="text-xs uppercase text-muted-foreground">Kitting packaging</p>
                    <p className="font-mono text-lg" data-testid="kit-packaging-cost">&#8377;{bom.pricing.kitting_packaging_cost.toFixed(2)}</p></div>
                  <div><p className="text-xs uppercase text-muted-foreground">Kit price / kit</p>
                    <p className="font-mono text-xl font-bold text-blue-800" data-testid="kit-price-per-kit">&#8377;{bom.pricing.kit_cost_per_kit.toFixed(4)}</p></div>
                  {bom.pricing.kit_weight_kg && (
                    <div className="sm:col-span-2 lg:col-span-4 text-xs text-muted-foreground" data-testid="kit-weight-reference">
                      Internal reference only: kit weight {bom.pricing.kit_weight_kg.toFixed(4)} kg
                      {bom.pricing.price_per_kg_reference ? ` · ₹${bom.pricing.price_per_kg_reference.toFixed(4)} / kg` : ''}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="card-dashboard">
            <CardHeader><CardTitle className="text-lg font-heading uppercase">Quantity</CardTitle></CardHeader>
            <CardContent>
              <div className="max-w-xs space-y-2">
                <Label>Quantity (kits)</Label>
                <Input type="number" value={kitFormData.quantity} data-testid="kit-quantity-input"
                  onChange={(e) => setKitFormData(prev => ({ ...prev, quantity: e.target.value }))} />
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end gap-4">
            <Button type="button" variant="outline" onClick={() => navigate('/costings')}>Cancel</Button>
            <Button type="submit" className="btn-primary" disabled={calculating || !bom.pricing?.is_priceable}
              data-testid="calculate-kit-costing-btn">
              <Calculator className="mr-2 h-4 w-4" />
              {calculating ? 'Saving...' : 'Create kit costing from BOM'}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}

